// src/core/store/auth.ts

import { defineStore } from 'pinia'
import { removeAuthHeader } from '@/core/config/axios'
import { ref, computed } from 'vue'
import {
  useLogin,
  useLogout,
  useRequestPasswordChange,
  useResetPassword,
} from '../api'
import { useRouter } from 'vue-router'
import { TUser } from '@/core/types'
import api from '@/core/config/axios'
import { isAxiosError } from 'axios'
import { useToggle } from '@vueuse/core'

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter()

  const user = ref<TUser | undefined>(undefined)
  const [isAuthenticating, toggleIsAuthenticating] = useToggle(true)

  const __isLoadingUser = ref(false)
  const __isSupportAccessSwitch = ref(false)

  const { mutateAsync: onRequestRecover, isPending: isRecoveryPassword } =
    useRequestPasswordChange({
      handlers: {
        onSuccess: async () => {
          router.replace({ name: 'passwordRecoveryConfirmation' })
        },
      },
    })

  const { mutateAsync: onResetPassword, isPending: isResetPasswordLocal } =
    useResetPassword({
      handlers: {
        onSuccess: async () => {
          router.replace({ name: 'login' })
        },
      },
    })

  const {
    mutateAsync: onLogin,
    isPending: isLogging,
    error: loginError,
  } = useLogin({
    handlers: {
      onSuccess: async (userData) => {
        if (userData?.ulid) {
          setUser(userData)
          await router.push({ name: 'dashboard' })
        }
      },
    },
  })

  const { mutateAsync: onLogout } = useLogout({
    handlers: {
      onSuccess: () => {
        toggleIsAuthenticating(true)
        onLogoutFn()
      },
      onError: () => {
        toggleIsAuthenticating(true)
        onLogoutFn()
      },
    },
  })

  const onLogoutFn = () => {
    user.value = undefined
    localStorage.removeItem('access_token')
    localStorage.removeItem('auth_access_token')
    removeAuthHeader()
    router.replace({ name: 'login', params: { logout: 'ok' } })
    toggleIsAuthenticating(false)
  }

  let sessionCheckPromise: Promise<void> | null = null

  const checkAuth = async () => {
    if (sessionCheckPromise) {
      return sessionCheckPromise
    }
    sessionCheckPromise = (async () => {
      __isLoadingUser.value = true
      try {
        const { data } = await api.get<TUser>('/v1/auth/user')
        if (data?.ulid) {
          setUser(data)
        } else {
          user.value = undefined
          toggleIsAuthenticating(false)
        }
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 401) {
          user.value = undefined
        }
        toggleIsAuthenticating(false)
      } finally {
        __isLoadingUser.value = false
        sessionCheckPromise = null
      }
    })()
    return sessionCheckPromise
  }

  const setUser = (payload: TUser) => {
    user.value = payload
    toggleIsAuthenticating(false)
  }

  const setSupportAccessSwitch = (value: boolean) => {
    __isSupportAccessSwitch.value = value
  }

  const isAuthenticated = computed(() => !!user.value)

  const loaders = computed(() => ({
    isLoadingUser: __isLoadingUser.value,
    isLogging: isLogging.value,
    isAuthenticating: isAuthenticating.value,
    isResetPassword: isResetPasswordLocal.value,
    isRecoveryPassword: isRecoveryPassword.value,
    isSupportAccessSwitch: __isSupportAccessSwitch.value,
  }))

  const errorsBag = computed(() => ({
    login: loginError?.value?.response?.data,
    reset: null,
    recovery: null,
  }))

  return {
    errorsBag,
    loaders,
    user,
    isAuthenticated,
    onResetPassword,
    onLogin,
    onLogout,
    setUser,
    setSupportAccessSwitch,
    checkAuth,
    onLogoutFn,
    onRequestRecover,
  }
})
